import React, { FC, useMemo, useState } from 'react';
import { Button, Input, Tooltip } from 'antd';
import { Divider, InfiniteScroll } from 'common/components';
import { Export } from 'common/svg';
import { AuthHooks } from 'common/hooks';
import { priceFormatter, showSuccessMessage, showWarningMessage } from 'common/helpers';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getIsSelfRegistered } from 'entities/Account/Account.helper';
import {
  BasketDeliveryDetails,
  BasketDeliveryModal,
  BasketGeneralInformation,
  BasketNotifications,
} from 'entities/Basket/components';
import {
  EDeliveryType,
  IBasket,
  IBasketGoods,
  ICreateBasketRequestPayload,
  IUpdateBasketPayload,
} from 'entities/Basket/Basket.models';
import { getBasketGeneralInformation, validateBasketDelivery } from 'entities/Basket/Basket.helper';
import {
  selectBasketDelivery,
  selectBasketStores,
  selectIsBasketLoading,
  updateBasket,
  uploadBasketExcel,
} from 'entities/Basket/Basket.slice';
import { selectCurrentUser, selectIsUserLoading, updateUser } from 'entities/User/User.slice';
import { IAddress } from 'entities/Address/Address.models';
import { ISubdivision } from 'entities/Subdivision/Subdivision.models';

interface IComponentProps {
  basket: IBasket | null;
  selectedGoods: IBasketGoods[];
  loading: boolean;
  onGoodsUpdate?: (basket: IBasket, userCityId?: number) => void;
  onCreateRequest?: (payload: Omit<ICreateBasketRequestPayload, 'goods'>) => void;
}

export const BasketOrderDetails: FC<IComponentProps> = ({ basket, selectedGoods, loading, onGoodsUpdate, onCreateRequest }) => {
  const dispatch = useAppDispatch();

  const isBasketLoading = useAppSelector(selectIsBasketLoading);
  const basketStores = useAppSelector(selectBasketStores);
  const basketDelivery = useAppSelector(selectBasketDelivery);
  const currentUser = useAppSelector(selectCurrentUser);
  const isUserLoading = useAppSelector(selectIsUserLoading);

  const [comment, setComment] = useState<string>('');
  const [openBasketDeliveryModal, setOpenBasketDeliveryModal] = useState<boolean>(false);
  const { isSeller } = AuthHooks.useIsSeller();
  const { totalAmount, isLimitExceeded } = useMemo(() => {
    return getBasketGeneralInformation({ goods: selectedGoods, basket, delivery: basketDelivery });
  }, [selectedGoods, basket, basketDelivery]);

  const isSelfRegistered = getIsSelfRegistered(basket?.account);
  const deliveryErrors = validateBasketDelivery(basket, basketStores, basketDelivery.deliveryInDays);
  const allowToCreateRequest = !isSeller && (!isSelfRegistered || (isSelfRegistered && !!basket?.deliveryType));
  const allowToSelectDeliveryMethod = !isSeller && isSelfRegistered && !basket?.deliveryType;
  const showDeliveryDetails = isSelfRegistered && !!basket?.deliveryType && !!selectedGoods.length;
  const allowToChangeDeliveryMethod = !isSeller && showDeliveryDetails;

  const changeSubdivision = async (subdivision: ISubdivision): Promise<void> => {
    if (!basket) {
      return;
    }

    const basketResponse = await dispatch(
      updateBasket({
        id: basket.id,
        subdivisionId: subdivision.id,
        ...(subdivision.legal && { legalId: subdivision.legal.id }),
        onSuccess: () => showSuccessMessage('Подразделение успешно изменено.'),
      }),
    );

    if (!basketResponse) {
      return;
    }

    if (currentUser && basketResponse.address?.cityId) {
      // Адрес подразделения в другом городе → город пользователя синхронизируется; предупреждаем.
      if (currentUser.city?.id !== basketResponse.address.cityId && basketResponse.address.city?.name) {
        showWarningMessage(`Адрес в другом городе — ваш город изменён на «${basketResponse.address.city.name}».`);
      }

      await dispatch(updateUser({ id: currentUser.id, cityId: basketResponse.address.cityId }));
      onGoodsUpdate?.(basketResponse, basketResponse.address.cityId);
    } else {
      onGoodsUpdate?.(basketResponse);
    }
  };

  const changeLegal = async (id: number): Promise<void> => {
    if (!basket) {
      return;
    }

    const basketResponse = await dispatch(
      updateBasket({
        id: basket.id,
        legalId: id,
        onSuccess: () => showSuccessMessage('Юридическое лицо успешно изменено.'),
      }),
    );

    if (!basketResponse) {
      return;
    }

    onGoodsUpdate?.(basketResponse);
  };

  const changeAddress = async (address: IAddress): Promise<void> => {
    if (!basket) {
      return;
    }

    const basketResponse = await dispatch(
      updateBasket({
        id: basket.id,
        addressId: address.id,
        onSuccess: () => showSuccessMessage('Адрес доставки успешно изменен.'),
      }),
    );

    if (!basketResponse) {
      return;
    }

    if (currentUser && basketResponse.address?.cityId) {
      // Адрес в другом городе → город пользователя синхронизируется с городом адреса; предупреждаем.
      if (currentUser.city?.id !== basketResponse.address.cityId && basketResponse.address.city?.name) {
        showWarningMessage(`Адрес в другом городе — ваш город изменён на «${basketResponse.address.city.name}».`);
      }

      await dispatch(updateUser({ id: currentUser.id, cityId: basketResponse.address.cityId }));
      onGoodsUpdate?.(basketResponse, basketResponse.address.cityId);
    } else {
      onGoodsUpdate?.(basketResponse);
    }
  };

  const handleCreateRequest = (): void => {
    onCreateRequest?.({
      comment: comment.length ? comment : undefined,
      onSuccess: () => {
        setComment('');
        setOpenBasketDeliveryModal(false);
      },
    });
  };

  const changeDeliveryDetails = (payload: Omit<IUpdateBasketPayload, 'id'>): void => {
    if (!basket) {
      return;
    }

    if (payload.deliveryType === EDeliveryType.Delivery) {
      payload.storeId = null;
    }

    if (payload.deliveryType === EDeliveryType.PickUp) {
      payload.comment = null;
    }

    dispatch(
      updateBasket({
        id: basket.id,
        ...payload,
        onSuccess: () => {
          setOpenBasketDeliveryModal(false);
        },
      }),
    );
  };

  const handleOpenBasketDeliveryModal = (): void => setOpenBasketDeliveryModal(true);

  const closeDeliveryOptionsModal = (): void => setOpenBasketDeliveryModal(false);

  if (!basket) {
    return null;
  }

  return (
    <div className="redesign relative h-full flex direction-column basket-order-details">
      <div className="p-11 flex align-center justify-end">
        {!isSeller && (
          <Tooltip title="Экспорт в Excel">
            <Button
              className="button-circle secondary"
              icon={<Export />}
              onClick={() => dispatch(uploadBasketExcel(basket.id))}
            />
          </Tooltip>
        )}
      </div>

      <div className="flex direction-column flex-1 basket-order-details__body">
        <InfiniteScroll>
          <div className="mb-9 ff-regular fs-xxl">Состав заказа</div>

          <BasketNotifications basket={basket} isLimitExceeded={isLimitExceeded} errors={deliveryErrors} />

          {allowToCreateRequest && (
            <Button
              className="button-lg primary w-full"
              loading={loading || isUserLoading || isBasketLoading}
              disabled={!selectedGoods.length || isLimitExceeded || (isSelfRegistered && !!deliveryErrors.length)}
              onClick={handleCreateRequest}
            >
              {`Оформить заказ на ${priceFormatter(totalAmount)}`}
            </Button>
          )}

          {allowToSelectDeliveryMethod && (
            <div>
              <Button
                className="button-lg primary w-full"
                loading={loading || isUserLoading || isBasketLoading}
                // Отсутствие адреса кнопку не лочит: самовывозу адрес не нужен, а вкладка
                // «Доставка» содержит собственный селектор адреса.
                disabled={!selectedGoods.length || isLimitExceeded}
                onClick={handleOpenBasketDeliveryModal}
              >
                Выбрать способ получения
              </Button>

              <div className="mt-4 ff-regular fs-s color-dark-grey">Выберите способ получения, чтобы оформить заказ</div>
            </div>
          )}

          {allowToChangeDeliveryMethod && (
            <div className="mt-4">
              <Button className="button-lg secondary w-full" onClick={handleOpenBasketDeliveryModal}>
                Изменить способ получения
              </Button>
            </div>
          )}

          <div className="mt-9">
            <BasketGeneralInformation
              basket={basket}
              selectedGoods={selectedGoods}
              isLimitExceeded={isLimitExceeded}
              onSubdivisionChange={changeSubdivision}
              onLegalChange={changeLegal}
              onAddressChange={changeAddress}
            />
          </div>

          {showDeliveryDetails && (
            <div>
              <Divider marginY={20} />

              <BasketDeliveryDetails basket={basket} onMethodChange={handleOpenBasketDeliveryModal} />
            </div>
          )}

          {!isSelfRegistered && (
            <div>
              <Divider marginY={20} />

              <Input.TextArea
                className="textarea"
                placeholder="Комментарий к заказу"
                maxLength={255}
                style={{ minHeight: 120 }}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          )}

          {basket?.comment && <div className="mt-6 ff-regular fs-s basket-order-details-comment">{basket.comment}</div>}

          <div style={{ marginBottom: 172 }} />
        </InfiniteScroll>
      </div>

      <BasketDeliveryModal
        open={openBasketDeliveryModal}
        basket={basket}
        selectedGoods={selectedGoods}
        delivery={basketDelivery}
        onAddressChange={changeAddress}
        onCancel={closeDeliveryOptionsModal}
        onSubmit={changeDeliveryDetails}
      />
    </div>
  );
};
